import { createSignal } from 'solid-js'
import { strFromU8, strToU8, unzipSync, zipSync, type Zippable } from 'fflate'
import type { Scene } from '../scene/types.ts'

/**
 * Projects live in IndexedDB, per browser. A project leaves the browser only as
 * a .lxc bundle: a zip of manifest.json, scene.json, thumbnail.png and
 * assets/<sha256>.<ext>, the same layout the old desktop build wrote, so its
 * bundles import unchanged.
 */

export interface ProjectSummary {
  id: string
  name: string
  updatedAt: string
  hasThumbnail: boolean
}

export interface AssetInfo {
  assetId: string
}

interface Manifest {
  formatVersion: number
  id: string
  name: string
  createdAt: string
  updatedAt: string
}

const FORMAT_VERSION = 1
const DB_NAME = 'lxcanvas'
const PROJECTS = 'projects'
const SCENES = 'scenes'
const THUMBNAILS = 'thumbnails'
const ASSETS = 'assets'

const CONTENT_TYPES: Record<string, string> = {
  '.png': 'image/png',
  '.jpg': 'image/jpeg',
  '.jpeg': 'image/jpeg',
  '.gif': 'image/gif',
  '.webp': 'image/webp',
  '.avif': 'image/avif',
  '.bmp': 'image/bmp',
  '.svg': 'image/svg+xml',
}

const ASSET_ID = /^[0-9a-f]{64}\.[a-z0-9]{1,5}$/

const [currentProjectId, setCurrentProjectId] = createSignal<string | null>(null)
export { currentProjectId, setCurrentProjectId }

// ------------------------------------------------------------------ IndexedDB

let dbPromise: Promise<IDBDatabase> | null = null

function db(): Promise<IDBDatabase> {
  dbPromise ??= new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, 1)
    req.onupgradeneeded = () => {
      const d = req.result
      d.createObjectStore(PROJECTS, { keyPath: 'id' })
      d.createObjectStore(SCENES)
      d.createObjectStore(THUMBNAILS)
      // Keyed [projectId, assetId], so a project's assets are one key range.
      d.createObjectStore(ASSETS)
    }
    req.onsuccess = () => resolve(req.result)
    req.onerror = () => reject(req.error)
  })
  return dbPromise
}

function done(tx: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve()
    tx.onerror = () => reject(tx.error)
    tx.onabort = () => reject(tx.error ?? new Error('Transaction aborted'))
  })
}

function result<T>(req: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result)
    req.onerror = () => reject(req.error)
  })
}

async function get<T>(store: string, key: IDBValidKey): Promise<T | undefined> {
  const tx = (await db()).transaction(store)
  return (await result(tx.objectStore(store).get(key))) as T | undefined
}

/** Write several stores in one transaction, so a project is never half-saved. */
async function write(stores: string[], fn: (tx: IDBTransaction) => void): Promise<void> {
  const tx = (await db()).transaction(stores, 'readwrite')
  fn(tx)
  await done(tx)
}

const assetRange = (projectId: string) => IDBKeyRange.bound([projectId], [projectId, []])

// ------------------------------------------------------------------ helpers

function clean(name: string): string {
  const trimmed = name.trim().slice(0, 120)
  return trimmed === '' ? 'Untitled' : trimmed
}

async function summary(m: Manifest): Promise<ProjectSummary> {
  const thumb = await get<Blob>(THUMBNAILS, m.id)
  return { id: m.id, name: m.name, updatedAt: m.updatedAt, hasThumbnail: !!thumb }
}

async function requireManifest(id: string): Promise<Manifest> {
  const m = await get<Manifest>(PROJECTS, id)
  if (!m) throw new Error(`No project ${id}`)
  return m
}

function extensionFor(filename: string, type: string): string {
  const dot = filename.lastIndexOf('.')
  const ext = dot === -1 ? '' : filename.slice(dot).toLowerCase()
  if (CONTENT_TYPES[ext]) return ext
  return Object.entries(CONTENT_TYPES).find(([, t]) => t === type)?.[0] ?? '.bin'
}

function typeFor(assetId: string): string {
  return CONTENT_TYPES[assetId.slice(assetId.lastIndexOf('.'))] ?? 'application/octet-stream'
}

async function sha256(bytes: ArrayBuffer): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', bytes)
  return [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, '0')).join('')
}

// ------------------------------------------------------------------ API

export const api = {
  /** Most recently edited first. */
  listProjects: async (): Promise<ProjectSummary[]> => {
    const tx = (await db()).transaction(PROJECTS)
    const all = (await result(tx.objectStore(PROJECTS).getAll())) as Manifest[]
    all.sort((a, b) => b.updatedAt.localeCompare(a.updatedAt))
    return Promise.all(all.map(summary))
  },

  createProject: async (name = 'Untitled'): Promise<ProjectSummary> => {
    const now = new Date().toISOString()
    const m: Manifest = {
      formatVersion: FORMAT_VERSION,
      id: crypto.randomUUID(),
      name: clean(name),
      createdAt: now,
      updatedAt: now,
    }
    await write([PROJECTS], (tx) => tx.objectStore(PROJECTS).put(m))
    return summary(m)
  },

  loadProject: async (id: string): Promise<{ summary: ProjectSummary; scene: Scene }> => {
    const m = await requireManifest(id)
    const scene = await get<Scene>(SCENES, id)
    if (!scene) throw new Error(`Project ${id} has no scene`)
    return { summary: await summary(m), scene }
  },

  renameProject: async (id: string, name: string): Promise<void> => {
    const m = await requireManifest(id)
    const next = { ...m, name: clean(name), updatedAt: new Date().toISOString() }
    await write([PROJECTS], (tx) => tx.objectStore(PROJECTS).put(next))
  },

  saveScene: async (id: string, scene: Scene): Promise<void> => {
    const m = await requireManifest(id)
    const next = { ...m, updatedAt: new Date().toISOString() }
    await write([PROJECTS, SCENES], (tx) => {
      tx.objectStore(SCENES).put(scene, id)
      tx.objectStore(PROJECTS).put(next)
    })
  },

  saveThumbnail: async (id: string, png: Blob): Promise<void> => {
    await write([THUMBNAILS], (tx) => tx.objectStore(THUMBNAILS).put(png, id))
  },

  thumbnail: (id: string): Promise<Blob | undefined> => get<Blob>(THUMBNAILS, id),

  deleteProject: async (id: string): Promise<void> => {
    await write([PROJECTS, SCENES, THUMBNAILS, ASSETS], (tx) => {
      tx.objectStore(PROJECTS).delete(id)
      tx.objectStore(SCENES).delete(id)
      tx.objectStore(THUMBNAILS).delete(id)
      tx.objectStore(ASSETS).delete(assetRange(id))
    })
  },

  /** Content-addressed, so the same image added twice is stored once. */
  uploadAsset: async (id: string, file: Blob, filename: string): Promise<AssetInfo> => {
    await requireManifest(id)
    const assetId = (await sha256(await file.arrayBuffer())) + extensionFor(filename, file.type)
    const blob = new Blob([file], { type: typeFor(assetId) })
    await write([ASSETS], (tx) => tx.objectStore(ASSETS).put(blob, [id, assetId]))
    return { assetId }
  },

  asset: (id: string, assetId: string): Promise<Blob | undefined> => get<Blob>(ASSETS, [id, assetId]),

  /** The project as a .lxc bundle. */
  exportProject: async (id: string): Promise<Blob> => {
    const m = await requireManifest(id)
    const scene = await get<Scene>(SCENES, id)
    const thumb = await get<Blob>(THUMBNAILS, id)

    const tx = (await db()).transaction(ASSETS)
    const store = tx.objectStore(ASSETS)
    const keys = (await result(store.getAllKeys(assetRange(id)))) as [string, string][]
    const blobs = (await result(store.getAll(assetRange(id)))) as Blob[]

    const files: Zippable = {
      'manifest.json': strToU8(JSON.stringify(m, null, 2)),
      'scene.json': strToU8(JSON.stringify(scene ?? null)),
    }
    // Images are already compressed; deflating them again only costs time.
    if (thumb) files['thumbnail.png'] = [new Uint8Array(await thumb.arrayBuffer()), { level: 0 }]
    for (const [i, [, assetId]] of keys.entries()) {
      files[`assets/${assetId}`] = [new Uint8Array(await blobs[i]!.arrayBuffer()), { level: 0 }]
    }
    return new Blob([zipSync(files)], { type: 'application/zip' })
  },

  /**
   * Import a .lxc bundle. It keeps its name but gets a fresh id, so importing a
   * copy of a project you already have cannot overwrite the original.
   */
  importProject: async (file: File): Promise<ProjectSummary> => {
    const entries = unzipSync(new Uint8Array(await file.arrayBuffer()))
    const sceneBytes = entries['scene.json']
    if (!sceneBytes) throw new Error(`${file.name} is not a project bundle`)
    const scene = JSON.parse(strFromU8(sceneBytes)) as Scene

    const prior = entries['manifest.json']
      ? (JSON.parse(strFromU8(entries['manifest.json'])) as Partial<Manifest>)
      : {}
    const now = new Date().toISOString()
    const m: Manifest = {
      formatVersion: FORMAT_VERSION,
      id: crypto.randomUUID(),
      name: clean(prior.name ?? file.name.replace(/\.lxc$/i, '')),
      createdAt: prior.createdAt ?? now,
      updatedAt: now,
    }

    await write([PROJECTS, SCENES, THUMBNAILS, ASSETS], (tx) => {
      tx.objectStore(PROJECTS).put(m)
      tx.objectStore(SCENES).put(scene, m.id)
      const thumb = entries['thumbnail.png']
      if (thumb) tx.objectStore(THUMBNAILS).put(new Blob([thumb], { type: 'image/png' }), m.id)
      for (const [path, bytes] of Object.entries(entries)) {
        const assetId = path.startsWith('assets/') ? path.slice('assets/'.length) : ''
        // Anything not shaped like an asset id is ignored, not stored.
        if (!ASSET_ID.test(assetId)) continue
        tx.objectStore(ASSETS).put(new Blob([bytes], { type: typeFor(assetId) }), [m.id, assetId])
      }
    })
    return summary(m)
  },
}
